import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  fetchLeadSession,
  updateLeadSessionName,
  verifyLeadSessionCode,
} from "./leadSessionClient";
import { meetingRequiresAuthentication } from "@/lib/productHasPrice";
import { LivestreamStartDateDisplay } from "./meeting/LivestreamStartDateDisplay";
import { MeetingDisplayNameGate } from "./meeting/MeetingDisplayNameGate";
import { WebsiteThemeScope } from "./WebsiteThemeScope";
import {
  parseWebsiteDesignSettingsTheme,
  type WebsiteDesignSettingsTheme,
} from "@/utils/websiteDesignTheme";

interface VerifyLocationState {
  sessionId?: string;
  delivery?: "phone" | "email";
}

type VerifyStep = "code" | "name";

export function MeetingAuthVerifyRoute() {
  const { id: productId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as VerifyLocationState;
  const [step, setStep] = useState<VerifyStep>("code");
  const [code, setCode] = useState("");
  const [suggestedDisplayName, setSuggestedDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [productName, setProductName] = useState("Meeting");
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [meetingStart, setMeetingStart] = useState<string | null>(null);
  const [theme, setTheme] = useState<WebsiteDesignSettingsTheme | null>(null);
  const sessionId = state.sessionId;

  useEffect(() => {
    if (!productId) return;

    if (!sessionId) {
      navigate(`/meetings/${productId}/login`, { replace: true });
      return;
    }

    const load = async () => {
      try {
        const [productRes, authRes] = await Promise.all([
          fetch(`/api/products/${productId}`),
          fetchLeadSession({ productId }),
        ]);

        if (authRes.ok) {
          const authData = (await authRes.json()) as { authenticated?: boolean };
          if (authData.authenticated) {
            navigate(`/meetings/${productId}`, { replace: true });
            return;
          }
        }

        if (productRes.ok) {
          const data = (await productRes.json()) as {
            product?: {
              name?: string;
              images?: { url?: string }[];
              price?: number;
              variants?: { price?: number }[];
              meeting?: { requireAuthentication?: boolean; start?: string | null };
            };
            websiteDesignSettings?: unknown;
          };
          if (!data.product || !meetingRequiresAuthentication(data.product)) {
            navigate(`/meetings/${productId}`, { replace: true });
            return;
          }
          setProductName(data.product.name?.trim() || "Meeting");
          setImageUrl(data.product.images?.[0]?.url);
          setMeetingStart(data.product.meeting?.start ?? null);
          setTheme(parseWebsiteDesignSettingsTheme(data.websiteDesignSettings));
        }
      } catch {
        setError("Failed to load meeting");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [productId, sessionId, navigate]);

  function goToMeeting() {
    if (!productId) return;
    navigate(`/meetings/${productId}`, { replace: true });
  }

  async function onSubmitCode(e: React.FormEvent) {
    e.preventDefault();
    if (!productId || !sessionId) return;

    const trimmed = code.trim();
    if (!trimmed) {
      setError("Code is required");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await verifyLeadSessionCode({
        productId,
        sessionId,
        code: trimmed,
      });
      const data = (await res.json()) as {
        error?: string;
        authenticated?: boolean;
        needsName?: boolean;
        suggestedDisplayName?: string;
      };
      if (!res.ok) {
        setError(data.error ?? "Invalid code");
        return;
      }
      if (data.needsName) {
        setSuggestedDisplayName(data.suggestedDisplayName?.trim() ?? "");
        setStep("name");
        return;
      }
      goToMeeting();
    } catch {
      setError("Network error");
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitDisplayName({ displayName }: { displayName: string }) {
    if (!productId) return;

    setSubmitting(true);
    setError(null);
    try {
      const res = await updateLeadSessionName({
        productId,
        displayName,
      });
      const data = (await res.json()) as { error?: string };
      if (!res.ok) {
        setError(data.error ?? "Failed to save display name");
        return;
      }
      goToMeeting();
    } catch {
      setError("Network error");
    } finally {
      setSubmitting(false);
    }
  }

  const deliveryLabel = state.delivery === "email" ? "email" : "phone";
  const pageTitle =
    step === "name" ? `${productName} · Display name` : `${productName} · Verify`;

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-black text-white">
          Loading...
        </div>
      </>
    );
  }

  if (step === "name") {
    return (
      <WebsiteThemeScope settings={theme}>
        <MeetingDisplayNameGate
          productName={productName}
          imageUrl={imageUrl}
          pageTitle={pageTitle}
          defaultDisplayName={suggestedDisplayName}
          description="Enter how you'd like to appear in the meeting"
          submitLabel="Continue"
          submitting={submitting}
          error={error}
          onSubmit={onSubmitDisplayName}
        />
      </WebsiteThemeScope>
    );
  }

  return (
    <WebsiteThemeScope settings={theme}>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <div className="min-h-screen text-white flex flex-col bg-black">
        <div className="flex flex-1 flex-col items-center justify-center px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(1.5rem,env(safe-area-inset-top))]">
          <div className="w-full max-w-sm space-y-6">
            <div className="text-center">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={productName}
                  className="mx-auto mb-4 h-40 w-40 rounded-lg object-cover shadow-xl"
                />
              ) : null}
              <h1 className="text-2xl font-bold">{productName}</h1>
              <LivestreamStartDateDisplay start={meetingStart} />
              <h2 className="mt-4 text-xl font-bold">Enter your code</h2>
              <p className="mt-2 text-sm text-gray-400">
                We sent a verification code to your {deliveryLabel}
              </p>
            </div>
            <form onSubmit={onSubmitCode} className="space-y-4">
              <div>
                <label htmlFor="code" className="block text-sm text-white mb-1">
                  Verification code
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  className="w-full rounded-lg bg-white/10 border border-white/20 px-3 py-2.5 text-white text-center text-2xl tracking-[0.3em] placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              {error ? <p className="text-sm text-red-400">{error}</p> : null}
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? "Verifying…" : "Verify"}
              </Button>
              <div className="text-center">
                <Link
                  to={`/meetings/${productId}/login`}
                  className="text-sm text-gray-400 underline underline-offset-2 hover:text-white inline-block"
                >
                  Send a new code
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </WebsiteThemeScope>
  );
}
